import { buildHref } from "@softmax/web-addresses";
import type { LeaguePublic } from "@softmax/observatory-client/types";
import { leagueRoute, slugifyLeagueName } from "@softmax/web-features/leagues";
import {
  curatedCoworldName,
  curatedText,
} from "@softmax/web-features/coworlds";

import { type GamePageContext } from "../../game-types";
import { resolveGameLeague } from "./league";

type PublicCoworldResolution =
  | Exclude<Awaited<ReturnType<typeof resolveGameLeague>>, { status: "ok" }>
  | {
      readonly status: "ok";
      readonly league: LeaguePublic;
      readonly coworldName: string;
      readonly displayName: string;
      readonly slug: string;
      readonly path: string;
      readonly route: ReturnType<typeof leagueRoute>;
      readonly coworldId: string | null;
      readonly leagueId: string;
      readonly divisionId: string | null;
      readonly description: string | null;
    };

function firstDivisionId(settings: LeaguePublic["settings"]): string | null {
  const ladder = settings?.ladder;

  if (typeof ladder !== "object" || ladder === null || !("divisions" in ladder))
    return null;

  const divisions = ladder.divisions;

  if (
    typeof divisions !== "object" ||
    divisions === null ||
    !("0" in divisions)
  )
    return null;

  const first = divisions["0"];

  if (typeof first !== "object" || first === null || !("division_id" in first))
    return null;

  return typeof first.division_id === "string" ? first.division_id : null;
}

export async function resolvePublicCoworld({
  signal,
  publicAddress,
  context = null,
}: {
  readonly signal?: AbortSignal | undefined;
  readonly publicAddress: string;
  readonly context?: GamePageContext | null;
}): Promise<PublicCoworldResolution> {
  const resolution = await resolveGameLeague({
    signal,
    publicAddress,
    context,
  });

  if (resolution.status !== "ok") return resolution;

  const { league } = resolution;
  const coworldName = league.game.coworld_name;

  if (!coworldName) return { status: "not-found" } as const;

  const game = league.game.game_address;

  if (!game) throw new Error(`Missing game address for league ${league.id}`);

  const route = leagueRoute(league);
  return {
    status: "ok" as const,
    league,
    coworldName,
    displayName: curatedCoworldName(coworldName) ?? league.game.name,
    slug: slugifyLeagueName(coworldName),
    path: buildHref({ name: "game.home", game }),
    route,
    coworldId:
      league.game.coworld_id ?? league.game.canonical_coworld_id ?? null,
    leagueId: league.id,
    divisionId: firstDivisionId(league.settings),
    description: league.game.description
      ? curatedText(league.game.description)
      : null,
  };
}
